import { CfdiIndexFilter, filterToSearchParams } from '../../models/CfdiIndex';

/**
 * CSV reports are served by our OWN back-end (express route `/services/reports/*.csv`)
 * and authenticated with the same session cookie GraphQL uses, hence `credentials: 'include'`.
 */

const resolveApiOrigin = (): string => {
    const httpUrl = process.env.REACT_APP_HTTP_URL;
    if (httpUrl) {
        try {
            return new URL(httpUrl).origin;
        } catch {
            // fall through
        }
    }
    return '';
};

export const REPORTS_BASE_URL = `${resolveApiOrigin()}/services/reports`;

export type ReportKind = 'facturas' | 'conceptos';

export const buildReportUrl = (kind: ReportKind, filter: CfdiIndexFilter): string =>
    `${REPORTS_BASE_URL}/${kind}.csv${filterToSearchParams(filter)}`;

const filenameFromDisposition = (disposition: string | null, fallback: string): string => {
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return match ? decodeURIComponent(match[1]) : fallback;
};

/**
 * Downloads a CSV report as a Blob and triggers the browser "save as".
 * Throws an Error with a user-readable message on failure (401 / 413 / 500).
 */
export const downloadReportCsv = async (kind: ReportKind, filter: CfdiIndexFilter, signal?: AbortSignal): Promise<string> => {
    const response = await fetch(buildReportUrl(kind, filter), { credentials: 'include', signal });
    if (!response.ok) {
        let message = `No fue posible generar el reporte (${response.status})`;
        try {
            const body = await response.json();
            if (body?.message) message = body.message;
        } catch {
            // Non-JSON error body, keep default message
        }
        throw new Error(message);
    }
    const blob = await response.blob();
    const filename = filenameFromDisposition(response.headers.get('content-disposition'), `${kind}.csv`);
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return filename;
};
